import { type CSSProperties, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Label, PageLayout, Textarea } from '@sentinel/ui';
import { type RevealSecretError, type RevealedSecret, useRevealSecret } from './use-reveal-secret';

/** "/nested/client_secret" -> "client_secret". */
export function secretLabel(pointer: string): string {
  const parts = pointer.split('/');
  return parts[parts.length - 1] || pointer;
}

/** Arrays (recovery codes) one per line; strings as-is; anything else as JSON. */
export function secretText(value: unknown): string {
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) return value.map((v) => (typeof v === 'string' ? v : JSON.stringify(v))).join('\n');
  return JSON.stringify(value);
}

const ERROR_KEYS = {
  generic: 'reveal.error_generic',
  impersonated: 'reveal.error_impersonated',
  unknown: 'reveal.error_unknown',
} as const;

const bodyStyle: CSSProperties = { display: 'grid', gap: '1rem', maxWidth: '40rem' };
const fieldStyle: CSSProperties = { display: 'grid', gap: '0.5rem' };

function SecretField({ pointer, value }: { pointer: string; value: unknown }) {
  const { t } = useTranslation('mcp');
  const inputId = useId();
  const [copied, setCopied] = useState(false);
  const text = secretText(value);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard rejection is silent: the value is still selectable.
    }
  }

  return (
    <div style={fieldStyle}>
      <Label htmlFor={inputId}>{secretLabel(pointer)}</Label>
      <Textarea
        id={inputId}
        data-testid="mcp-reveal-value"
        readOnly
        value={text}
        rows={Math.min(Math.max(text.split('\n').length, 1), 12)}
        spellCheck={false}
      />
      <div>
        <Button type="button" data-testid="mcp-reveal-copy" onClick={() => void copy()}>
          {copied ? t('reveal.copied') : t('reveal.copy')}
        </Button>
      </div>
    </div>
  );
}

export function McpRevealPage({ id }: { id: string }) {
  const { t } = useTranslation('mcp');
  const reveal = useRevealSecret();
  const [revealed, setRevealed] = useState<RevealedSecret | null>(null);
  // `gone`: unknown, expired, used, or another account's (the server cannot
  // say which). The last case is fixed by signing in as the owner, so the
  // button stays for a retry after switching accounts. `lost`: consumed by
  // this request but not shown; it cannot be retried, so the button goes.
  const [gone, setGone] = useState<'gone' | 'lost' | null>(null);
  // `generic`: the request was refused before the secret was touched (MCP
  // switched off, signed out, rate limited). `impersonated`: refused for this
  // session only; the secret is intact for its owner. `unknown`: a server
  // error or a lost response, after which the secret may already be consumed.
  const [error, setError] = useState<'generic' | 'impersonated' | 'unknown' | null>(null);

  // Deliberately not on mount: link previews would consume the one-time secret.
  async function handleShow() {
    // A retry after `gone` (for example as the owner) must not keep the old
    // message next to a secret or a different error.
    setGone(null);
    setError(null);
    try {
      setRevealed(await reveal.mutateAsync({ id }));
    } catch (e) {
      const { status, body } = e as RevealSecretError;
      const code = body?.error;
      if (code === 'mcp_secret_not_found') setGone('gone');
      else if (code === 'mcp_secret_lost') setGone('lost');
      // A retry in this session can never work, but the secret is untouched.
      else if (code === 'mcp_secret_impersonated') setError('impersonated');
      // A server error or no response at all can come after the secret was
      // taken (the reply was lost on the way back), so say it may be gone; a
      // retry then answers which. Either way keep the button.
      else if (status === undefined || status >= 500) setError('unknown');
      else setError('generic');
    }
  }

  return (
    <PageLayout title={t('reveal.title')} subtitle={t('reveal.description')}>
      <div style={bodyStyle}>
        {gone ? (
          <Alert type="warning" role="status" data-testid="mcp-reveal-gone">
            {t(gone === 'lost' ? 'reveal.lost' : 'reveal.gone')}
          </Alert>
        ) : null}
        {error ? (
          <Alert type="error" role="alert">
            {t(ERROR_KEYS[error])}
          </Alert>
        ) : null}
        {revealed ? (
          <>
            <Alert type="warning" role="status">
              {t('reveal.warning')}
            </Alert>
            {Object.entries(revealed.secrets).map(([pointer, value]) => (
              <SecretField key={pointer} pointer={pointer} value={value} />
            ))}
          </>
        ) : null}
        {!revealed && gone !== 'lost' ? (
          <div>
            <Button
              type="button"
              data-testid="mcp-reveal-show"
              onClick={() => void handleShow()}
              disabled={reveal.isPending}
            >
              {t('reveal.show')}
            </Button>
          </div>
        ) : null}
      </div>
    </PageLayout>
  );
}
